import { Box, Collapse, FormControl, Grid, InputLabel, MenuItem, Select, TableCellProps, Tooltip, Typography, useTheme } from "@mui/material"
import { useEffect, useState } from "react"
import { PartnerService } from "../../../services/partner"
import { LineComponent } from "../../../components/line"
import { Format } from "../../../utils"
import useAuth from "../../../context/auth"
import { TableComponent } from "../../../components/table"

export interface IContract {
    id: number
    uuid: string
    clientId: number
    commissionPaid: boolean
    haveCommission: boolean
    netAmount: number
    groosAmount: number
    total: number
    rent: number
    parcels: number
    lateCharges: number
    totalPaid: number
    totalDebit: number
    payTo: string
    status: number
    integrations: any
    createdAt: string
    canceledAt: any
    canceledReason: any
    approvedAt: any
    signedAt: any
    canSignUntil: any
    transferAt: any
    lastPaymentAt: any
    name: string
    cpf: string
    score: number
    clientStatus: number
}

type SimplifiedContract = Pick<IContract, 'id' | 'createdAt' | 'name' | 'netAmount' | "status" | "canceledReason">

export const statusList = {
    1: "Aguardando análise",
    5: "Pendente de assinatura",
    20: "Contrato aprovado",
    30: "Contrato aprovado",
    35: "Contrato aprovado",
    40: "Contrato finalizado",
    50: "Não aprovado",
}

export const translateStatus = (_status: number): string => {
    if (statusList.hasOwnProperty(_status)) {
        return statusList[_status as keyof typeof statusList];
    } else {
        return "STATUS DESCONHECIDO";
    }
}

export const PartnerContracts = () => {
    const { palette } = useTheme()
    const { isMobile } = useAuth()

    const [contracts, setContracts] = useState<IContract[]>([])
    const [status, setStatus] = useState<number[]>([1, 5, 10, 20, 30, 35, 40, 50])
    const [loading, setLoading] = useState(false)
    const excludeStatusList = [30, 35]


    const getContracts = () => {
        setLoading(true)
        PartnerService.listContract(status)
            .then(({ data }) => {
                setContracts(data?.contracts)
            })
            .finally(() => setLoading(false))
    }

    const MobileContract = ({ contract: _contract }: { contract: IContract }) => {
        return (
            <Box
                component={"button"}
                sx={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "10px",
                    p: "1.5rem",
                    width: "100%",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: ".5rem",
                    border: `1px solid ${palette.primary.light}`
                }}
            >
                <Typography fontSize={"1.4rem"} fontWeight={700} textAlign={"left"}>{_contract?.name}</Typography>
                <LineComponent usedInModal height="1px" margin="1rem 1rem" />

                <Typography fontSize={"1.4rem"} color="#707070" textAlign={"left"}><strong>ID: </strong>{_contract?.id}</Typography>
                <Typography fontSize={"1.4rem"} color="#707070" textAlign={"left"}><strong>Data da solicitação: </strong>{Format.date(_contract?.createdAt)}</Typography>
                <Typography fontSize={"1.4rem"} color="#707070" textAlign={"left"}><strong>Valor: </strong>{Format.currency(_contract?.netAmount)}</Typography>
                <Typography fontSize={"1.4rem"} color={_contract.status === 50 ? "red" : [20, 30, 35].includes(20) ? "green" : _contract.status === 30 ? "orange" : _contract.status === 40 ? "black" : "#707070"} textAlign={"left"}><strong style={{ color: "#707070" }}>Status: </strong >{translateStatus(_contract?.status)}</Typography>
                {_contract.status === 50 && _contract.canceledReason && <Typography fontSize={"1.4rem"} color="#707070" textAlign={"left"}><strong>Motivo da reprovação: </strong>{_contract.canceledReason}</Typography>}
            </Box >
        )
    }

    const reorderProperties = (array: IContract[]): SimplifiedContract[] => {
        return array.map((item) => ({
            id: item.id,
            createdAt: item.createdAt,
            name: item.name,
            netAmount: item.netAmount,
            status: item.status,
            canceledReason: item?.canceledReason
        }))
    }

    useEffect(() => {
        getContracts()
    }, [status])

    return (
        <Box
            sx={{
                maxWidth: "150rem",
                margin: "0 auto",
                ...(!isMobile && {
                    padding: "0 2rem"
                })
            }}
        >
            <Typography fontSize={"2rem"} fontWeight={600} mb={"2rem"}>Contratos</Typography>
            <Grid
                mb={2}
                mt={2}
                container
                justifyContent={"flex-end"}
            >
                <FormControl sx={{
                    width: isMobile ? "90vw" : "70vw",

                }}>
                    <InputLabel id="select-kind-label">Filtrar por status</InputLabel>
                    <Select
                        labelId="select-kind-label"
                        id="select-kind-id"
                        value={status}
                        label="Filtrar por status"
                        onChange={(e) => setStatus(e.target.value as any)}
                        multiple
                        sx={{
                            minHeight: "5.6rem",
                        }}
                    >
                        {Object.keys(statusList).filter((item) => !excludeStatusList.includes(Number(item))).map((key: string) => {
                            const numericKey = Number(key);
                            return (
                                <MenuItem value={numericKey} key={numericKey}>{statusList[numericKey as keyof typeof statusList]}</MenuItem>
                            )
                        })}
                    </Select>
                </FormControl>
            </Grid>

            {!isMobile && <TableComponent
                head={[
                    {
                        label: "ID",
                        placement: "left"
                    },
                    {
                        label: "Data de solicitação",
                        placement: "left"
                    },
                    {
                        label: "Cliente",
                        placement: "left"
                    },
                    {
                        label: "Valor",
                        placement: "left"
                    },
                    {
                        label: "Status",
                        placement: "left"
                    },
                ]}
                body={reorderProperties(contracts).map((row) => {
                    const items: {
                        label: string,
                        placement?: TableCellProps["align"],
                        customized?: JSX.Element
                    }[] = []
                    for (const key of Object.keys(row) as (keyof SimplifiedContract)[]) {

                        if (key === "netAmount") {
                            items.push({
                                label: Format.currency(row[key])
                            })
                        } else if (key === "status") {
                            items.push({
                                label: translateStatus(row[key]),
                                customized: <Tooltip title={row["canceledReason"]} sx={{ cursor: "pointer" }}>
                                    <Typography fontWeight={700} color={row[key] === 50 ? "red" : [20, 30, 35].includes(20) ? "green" : row[key] === 30 ? "orange" : row[key] === 40 ? "black" : "#707070"} textAlign={"left"}>{translateStatus(row[key])}</Typography>
                                </Tooltip>
                            })
                        } else if (key === "createdAt") {
                            items.push({
                                label: Format.dateWithTime(row[key])
                            })
                        } else {
                            if (key !== "canceledReason") {
                                items.push({
                                    label: row[key]?.toString()
                                })
                            }
                        }

                    }
                    return items
                })}
                // onRowClick={(rowIndex, row) => {
                //     navigate(`/contratos/${row[0]}`)
                // }}
                loading={loading}
            />}
            {isMobile &&
                <Collapse in={contracts?.length > 0}>
                    <Grid
                        display={"flex"}
                        direction={"column"}
                        gap={"1.5rem"}
                    >
                        {contracts.map((c) => <MobileContract contract={c} />)}
                    </Grid>
                </Collapse>
            }

            {(isMobile && contracts?.length === 0) &&
                <Typography textAlign={"center"} sx={{ opacity: .5 }} mt={"5rem"}>Nenhum contrato encontrado...</Typography>
            }

        </Box>
    )
}